import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import HeaderNotifications from './HeaderNotifications';

interface HeaderProps {
  onToggleSidebar: () => void;
}

const getCategoryFromPath = (pathname: string): string => {
  // HR related paths
  const hrPaths = [
    '/employees', '/ranks', '/candidates', '/crew-certificates', '/crew-change',
    '/crew-list', '/crew-list-arabic', '/active-crew-list-arabic', '/personnel-letters',
    '/personnel-coast-guard-permits', '/cars', '/car-letters', '/car-coast-guard-permits',
    '/document-settings', '/hiring', '/deactivated-employees', '/raaft-letters', '/taeen-letters'
  ];
  
  // Finance related paths
  const financePaths = [
    '/banks', '/bills', '/categories', '/clients', '/contracts', '/deductions',
    '/delivery-notes', '/foa', '/invoices', '/invoice-hire', '/payment-order',
    '/petty-cash', '/providers', '/purchases', '/requisitions', '/safes',
    '/debit-notes', '/finance-reports', '/reports'
  ];

  // System Administration paths
  const systemPaths = [
    '/roles', '/system-users', '/branches'
  ];
  
  if (hrPaths.some(path => pathname.startsWith(path))) {
    return 'Human Resources Management';
  } else if (financePaths.some(path => pathname.startsWith(path))) {
    return 'Finance';
  } else if (systemPaths.some(path => pathname.startsWith(path))) {
    return 'System Administration';
  }
  
  return 'Marine Logistics Management';
};

const Header: React.FC<HeaderProps> = ({ onToggleSidebar }) => {
  const location = useLocation();
  const categoryTitle = getCategoryFromPath(location.pathname);
  const { user } = useAuth();

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.toUpperCase() || fullName[0]?.toUpperCase() || 'U';
  const roleName = user?.role?.name || '';

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center space-x-3">
          <button
            className="lg:hidden text-gray-500 hover:text-gray-700"
            onClick={onToggleSidebar}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-800 hidden sm:block">
              {categoryTitle}
            </h2>
            {/* Category Badge - Always visible */}
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
              categoryTitle === 'Finance' 
                ? 'bg-green-100 text-green-800' 
                : categoryTitle === 'Human Resources Management'
                ? 'bg-blue-100 text-blue-800'
                : categoryTitle === 'System Administration'
                ? 'bg-purple-100 text-purple-800'
                : 'bg-ocean-100 text-ocean-800'
            }`}>
              {categoryTitle === 'Human Resources Management' ? 'HR' :
               categoryTitle === 'Finance' ? 'FIN' :
               categoryTitle === 'System Administration' ? 'SYS' : 'MARINE'}
            </span>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <HeaderNotifications />

          {/* User Profile */}
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-ocean-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">{initials}</span>
            </div>
            <div className="hidden sm:block">
              <p className="text-sm font-medium text-gray-700">{fullName}</p>
              <p className="text-xs text-gray-500">{roleName}</p>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

 